import { Injectable, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { isActionVisible } from '../../../../../../core/actions/app-action';
import type { AppAction } from '../../../../../../core/actions/app-action';
import { AppActionRegistryService } from '../../../../../../core/actions/app-action-registry.service';
import { canManageActionSet } from '../../../../../../core/constants/action-catalogue';
import { APP_CATALOG, APP_CATEGORY_LABELS } from '../../../../../../core/constants/app-catalog';
import { MANAGE_APP_SYNONYMS, WORKSPACE_SYNONYMS } from '../../../../../../core/constants/workspace-synonyms';
import { manageUrl } from '../../../../../../core/guards/manage-redirect';
import { appHostname } from '../../../../../../core/models/app-link.model';
import type { QuickLink } from '../../../../../../core/models/home-dashboard.model';
import { LocaleService } from '../../../../../../core/services/locale.service';
import { QuickLinksService } from '../../../../../../core/services/quick-links.service';
import { isUnauthenticatedSurface } from '../../../../../../core/utils/unauthenticated-surface';
import type { CmdkAppItem, CmdkItem } from '../../models/cmdk.model';
import type { CmdkProvider, CmdkProviderContext } from '../../models/cmdk-provider';

/**
 * RESPONSIBILITY: the "Apps" scope — every app the viewer can open. First
 * RICOH Workplace's own (Map, Schedule, Flow, Visitors, Insights, Settings,
 * Manage …, as far as their capabilities and grants allow), then every quick
 * link they have, merged from the organisation, their division, their
 * location and their own personal list.
 *
 * Until 9 Oct 2026 this was the quick links alone, so "settings", "insights"
 * or "visitors" under Apps found nothing unless an admin had happened to add
 * a link by that name, and "admin" and "manage" found nothing anywhere.
 *
 * Local and synchronous. `QuickLinksService` is a root singleton that loads in
 * its own constructor and is injected at bootstrap (`app.ts`), so the
 * catalogue is already warm by the time anyone presses ⌘K. The provider does
 * no network work of its own — the contract forbids it, and there is nothing
 * to fetch.
 *
 * `idle: 'hide'` — the merged list runs to dozens of entries and would bury
 * the suggested actions. The Apps chip and the `:` prefix browse it in full.
 *
 * BACKEND CONTRACT: none directly. `QuickLinksService` wraps `quickLinks` and
 * `myQuickLinks`.
 *
 * AI CONTEXT — two things that are easy to get wrong
 *
 * **Aliases carry the searchability.** The ranker's haystack is title,
 * subtitle and aliases only (`models/cmdk-ranking.ts`) — the URL is never
 * scored. Without aliases, typing "atlassian" or "chat" finds nothing, and
 * typing a hostname finds nothing either. They score at the ALIAS tier, below
 * a title substring, which is the right ordering for free.
 *
 * **The whole `QuickLink` is needed to launch, not its id.**
 * `AppLaunchService.launch()` takes the link object, so the provider keeps a
 * map rather than making the palette look it up again.
 */
/** Paper registry icons for Workplace's apps (some nav icons are not in it). */
const WORKPLACE_APP_ICONS: Readonly<Record<string, string>> = {
  home: 'home',
  map: 'map',
  discover: 'newspaper',
  schedule: 'calendar-range',
  flow: 'layers',
  visitors: 'people',
  insights: 'bar-chart',
  content: 'newspaper',
  'live-events': 'video',
  media: 'image',
  games: 'trophy',
  world: 'globe',
  settings: 'settings',
  manage: 'sliders',
};

@Injectable({ providedIn: 'root' })
export class CmdkAppProvider implements CmdkProvider {
  private readonly quickLinks = inject(QuickLinksService);
  private readonly router = inject(Router);
  private readonly appActions = inject(AppActionRegistryService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly id = 'app';
  readonly kind = 'app' as const;
  readonly scope = 'apps' as const;
  readonly minQueryLength = 0;
  readonly idle = 'hide' as const;

  /** id → the link itself, so activation does not have to re-resolve it. */
  private readonly byId = new Map<string, QuickLink>();

  /**
   * Changes whenever the underlying links or their load state change.
   *
   * `CmdkSearchService` gathers providers inside `untracked()` so that a
   * command re-registration cannot invalidate the row list mid-typing. That is
   * right for the synchronous registry and WRONG for anything asynchronous:
   * `allLinks()` resolves after a network load, and without a tracked
   * dependency a palette opened during that load would show an empty Apps
   * group forever. This is the one signal the search service reads tracked.
   */
  readonly revision = computed(
    () =>
      `${this.quickLinks.allLinks().length}:${
        this.hasError() ? 'err' : 'ok'
      }:${this.quickLinks.loading()}:${this.workplaceApps().map(app => app.id).join(',')}`,
  );

  /**
   * RICOH Workplace's own apps this viewer can open: each workspace whose
   * capability AND grant gates pass (the app menu's own rule, failing closed
   * until the flags load). Reactive, so a palette opened before the flags
   * arrived fills in. Manage is added per search (`collect`), from the
   * actions being searched with.
   */
  private readonly workplaceApps = computed<CmdkAppItem[]>(() => {
    this.locale.locale();
    const ctx = this.appActions.context();
    const apps: CmdkAppItem[] = [];
    for (const action of this.appActions.actions()) {
      // An alias (Projects → Flow) is the same app twice. NOT `menu === false`:
      // that also marks Visitors and Insights, which left the app menu on
      // 11 Oct 2026 because Home is their door, and stay apps here.
      if (!action.workspaceId || action.aliasOf) continue;
      if (!isActionVisible(action, ctx)) continue;
      apps.push(this.toWorkplaceApp(action, action.workspaceId));
    }
    return apps;
  });

  isEnabled(): boolean {
    return true;
  }

  /** Re-run the org + personal link load. Wired to the error state's retry. */
  async reload(): Promise<void> {
    await this.quickLinks.loadLinks();
  }

  browse(ctx: CmdkProviderContext): CmdkItem[] {
    return this.collect(ctx);
  }

  search(ctx: CmdkProviderContext): CmdkItem[] {
    // The ranker does the matching; the provider hands over the catalogue.
    return this.collect(ctx);
  }

  /** The `QuickLink` behind a row, for `AppLaunchService.launch()`. */
  linkFor(id: string): QuickLink | undefined {
    return this.byId.get(id);
  }

  /**
   * True when the org links failed to load.
   *
   * This matters more than it looks. On failure `QuickLinksService`
   * substitutes six hardcoded fallback links (SharePoint, ServiceNow,
   * SuccessFactors, Jira, Confluence, Teams) *and* sets its error — so a
   * palette that only read `allLinks()` would confidently offer six apps the
   * tenant may not own. The results pane surfaces this as a per-group error
   * banner instead.
   */
  hasError(): boolean {
    if (this.quickLinks.error() !== null) return true;
    // Public preview never fetches org links (`QuickLinksService` skips
    // unauthenticated surfaces). An empty catalogue with no error would
    // look like "no matches" or, worse, the hardcoded fallback apps.
    // Treat that skipped load as a failed source — the same reading the
    // e2e harness asserts on `/preview/cmdk`.
    return (
      !this.quickLinks.loading() &&
      this.quickLinks.allLinks().length === 0 &&
      isUnauthenticatedSurface(`#${this.router.url}`)
    );
  }

  private collect(ctx: CmdkProviderContext): CmdkItem[] {
    this.byId.clear();
    const links = this.quickLinks.allLinks();
    const manage = this.manageApp(ctx.userActions);

    return [
      ...this.workplaceApps(),
      ...(manage ? [manage] : []),
      ...links.map(link => {
        this.byId.set(link.id, link);
        return toAppItem(link);
      }),
    ];
  }

  private toWorkplaceApp(action: AppAction, workspaceId: string): CmdkAppItem {
    const description = action.descriptionKey
      ? this.translateOr(action.descriptionKey, action.description ?? '')
      : action.description;
    return {
      id: `workplace:${workspaceId}`,
      kind: 'app',
      title: this.appActions.label(action),
      subtitle: description || undefined,
      // The English label too, so "insights" still finds Insights in a
      // translated interface.
      aliases: [action.label, ...action.keywords, ...(WORKSPACE_SYNONYMS[workspaceId] ?? [])],
      url: '',
      actionId: action.id,
      workspaceId,
      icon: WORKPLACE_APP_ICONS[workspaceId] ?? 'grid',
    };
  }

  /**
   * Manage, the operations console, on its own origin. Offered to anyone who
   * manages something — Manage enforces its own sign-in and grants, so this is
   * a door, not a permission.
   */
  private manageApp(userActions: readonly string[]): CmdkAppItem | null {
    // The same rule as `ProfileDataService.canManageSomething`, over the
    // actions being searched with.
    if (!canManageActionSet(userActions)) return null;
    const url = manageUrl('/');
    if (!url) return null;
    return {
      id: 'workplace:manage',
      kind: 'app',
      title: 'Manage',
      subtitle: appHostname(url),
      aliases: [...MANAGE_APP_SYNONYMS],
      url,
      external: true,
      icon: WORKPLACE_APP_ICONS['manage'],
    };
  }

  private translateOr(key: string, fallback: string): string {
    const value = this.transloco.translate(key);
    return value && value !== key ? value : fallback;
  }
}

/** The catalogue entry behind a link's provider key, if it has one. */
function catalogueEntry(link: QuickLink) {
  return link.provider ? APP_CATALOG.find(entry => entry.id === link.provider) : undefined;
}

export function toAppItem(link: QuickLink): CmdkAppItem {
  const catalogue = catalogueEntry(link);
  const hostname = appHostname(link.url);
  const categoryLabel = catalogue ? APP_CATEGORY_LABELS[catalogue.category] : undefined;

  return {
    id: link.id,
    kind: 'app',
    title: link.label,
    // The hostname, not the description. It is the one line that tells you
    // WHERE this goes, which is the question a launcher row has to answer.
    subtitle: hostname,
    aliases: [
      hostname,
      // The catalogue's canonical name — "Microsoft Teams" finds a link the
      // tenant labelled just "Teams", and vice versa.
      catalogue?.name,
      categoryLabel,
      link.description,
    ].filter((value): value is string => Boolean(value)),
    provider: link.provider,
    category: categoryLabel,
    url: link.url,
  };
}
